/**
 * Workspace Registry
 *
 * Maps workspace types to their corresponding tab components.
 * This allows Workspace.tsx to render tabs without hardcoded conditionals.
 */

import { ComponentType } from 'react';
import type { WorkspaceType, WorkspaceTabComponentProps } from '../types/workspaceTypes';

/**
 * A component that renders a workspace tab
 */
export type WorkspaceTabComponent = ComponentType<WorkspaceTabComponentProps>;

/**
 * Registry mapping workspace types to their components
 */
const registry = new Map<WorkspaceType, WorkspaceTabComponent>();

/**
 * Register a workspace component for a given type.
 * Called during module initialization by each workspace type.
 */
export function registerWorkspaceComponent(
  type: WorkspaceType,
  component: WorkspaceTabComponent
): void {
  registry.set(type, component);
}

/**
 * Get the component for a workspace type.
 * Returns undefined if no component is registered.
 */
export function getWorkspaceComponent(
  type: WorkspaceType
): WorkspaceTabComponent | undefined {
  return registry.get(type);
}

/**
 * Check if a workspace type has a registered component.
 */
export function hasWorkspaceComponent(type: WorkspaceType): boolean {
  return registry.has(type);
}

/**
 * Get all registered workspace types.
 */
export function getRegisteredWorkspaceTypes(): WorkspaceType[] {
  return Array.from(registry.keys());
}
